import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader, Spinner, Surface, CopyButton } from "@/components/common";
import { RefinePanel } from "@/components/RefinePanel";
import { AttachmentUploader } from "@/components/AttachmentUploader";
import { LanguageSelect } from "@/components/LanguageSelect";
import { useLocale } from "@/context/LocaleContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function DebatePage() {
  const location = useLocation();
  const { t, contentLang } = useLocale();
  const [topic, setTopic] = useState("");
  const [result, setResult] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [language, setLanguage] = useState(contentLang);

  useEffect(() => {
    if (location.state?.topic) {
      setTopic(location.state.topic);
      generate(location.state.topic);
    }
    // eslint-disable-next-line
  }, []);

  const generate = async (arg) => {
    const value = typeof arg === "string" ? arg : topic;
    if (!value.trim()) return toast.error(t("Skriv hvad debatindlægget skal handle om"));
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/debate", { topic: value, language, attachment_ids: attachments.map((a) => a.id) });
      setResult(data.result);
      setProjectId(data.project_id);
      toast.success(t("Dit debatindlæg er klar — gemt automatisk"));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader overline="Debatindlæg" title="Sæt dagsordenen med et skarpt debatindlæg"
        subtitle="Beskriv emnet og jeres holdning — så får du et gennemarbejdet, overbevisende debatindlæg i jeres tone." />

      <div className="space-y-3 mb-8">
        <Textarea data-testid="debate-topic" value={topic} onChange={(e) => setTopic(e.target.value)}
          rows={3} placeholder={t("Hvad skal debatindlægget handle om — og hvad er jeres holdning?")} />
        <AttachmentUploader files={attachments} onChange={setAttachments} disabled={loading} />
        <div className="flex flex-wrap items-center gap-3">
          <LanguageSelect value={language} onChange={setLanguage} disabled={loading} testid="debate-language" />
          <Button onClick={() => generate()} disabled={loading} className="gap-2" data-testid="debate-generate-btn">
            <Sparkles className="h-4 w-4" /> {t("Skriv debatindlæg")}
          </Button>
        </div>
      </div>

      {loading && <Spinner label="Skriver jeres debatindlæg..." />}
      {result && (
        <Surface testid="debate-result">
          <div className="flex justify-end mb-3">
            <CopyButton text={`${result.title || ""}\n\n${result.debate || ""}`.trim()} testid="debate-copy" />
          </div>
          <Input value={result.title || ""} onChange={(e) => setResult({ ...result, title: e.target.value })}
            className="font-display text-xl font-medium border-0 bg-transparent px-0 mb-4 focus-visible:ring-0"
            placeholder={t("Overskrift")} data-testid="debate-title" />
          <Textarea value={result.debate || ""} onChange={(e) => setResult({ ...result, debate: e.target.value })}
            rows={20} className="font-sans leading-relaxed resize-none border-0 bg-transparent px-0 focus-visible:ring-0" data-testid="debate-text" />
        </Surface>
      )}
      {result && (
        <div className="mt-4">
          <RefinePanel result={result} setResult={setResult} module="debate" projectId={projectId} language={language}
            quick={["Gør det skarpere", "Kortere", "Mere sagligt", "Stærkere afslutning", "Tilføj et modargument"].map((q) => t(q))} />
        </div>
      )}
    </div>
  );
}
